import { styled, lighten } from '@mui/material/styles';
import { AppBar, Button, Toolbar, Typography } from '@mui/material';

// Outer wrapper exposes Form className strings -------------------------------
export const RootWrap = styled('div', {
  shouldForwardProp: (prop) => prop !== 'canEdit',
})(({ theme, canEdit }) => ({
  '& .task-detail-form-style': {
    alignItems: 'flex-start !important',
    position: 'relative',
    maxWidth: '1000px !important',
    overflow: 'visible !important',
    height: 'auto',
  },
  '& .task-detail-title-style': {
    fontSize: '18px !important',
    fontWeight: 'bold',
  },
  '& .task-detail-screen-width': {
    width: '100%',
    '& .MuiInput-formControl.Mui-disabled, .MuiInputLabel-formControl': {
      color: theme.palette.text.primary,
    },
    '& .Mui-disabled.MuiInputBase-formControl .MuiSelect-icon': {
      display: 'none',
    },
    '& .Mui-disabled.MuiInput-underline:before': {
      border: `none !important`,
    },
    ...(canEdit
      ? {}
      : {
          '& .inputEach': {
            borderBottom: `1px solid ${theme.palette.action.disabledBackground} !important`,
          },
        }),
  },
  '& .task-detail-screen-container': {
    padding: 0,
  },
  '& .task-detail-screen-container-mobile': {
    maxWidth: 'none !important',
  },
}));

export const TD_FORM_STYLE = 'task-detail-form-style';
export const TD_TITLE_STYLE = 'task-detail-title-style';
export const TD_SCREEN_WIDTH = 'task-detail-screen-width';
export const TD_SCREEN_CONTAINER = 'task-detail-screen-container';
export const TD_SCREEN_CONTAINER_MOBILE = 'task-detail-screen-container-mobile';

export const StyledAppBar = styled(AppBar)({
  position: 'relative',
});

export const StyledToolbar = styled(Toolbar)({
  height: 70,
});

export const Title = styled(Typography)({
  margin: 0,
  padding: 0,
  lineHeight: 1.2,
  fontWeight: 'bold',
});

export const Status = styled(Typography)({
  fontWeight: 'bold',
  alignItems: 'center',
  justifyContent: 'center',
  flexDirection: 'row',
  display: 'flex',
});

export const StatusIcon = styled('span')(({ theme }) => ({
  marginLeft: theme.spacing(2),
  display: 'inline-flex',
  alignItems: 'center',
}));

export const Container = styled('div')(({ theme }) => ({
  position: 'relative',
  padding: theme.spacing(4),
  width: '80%',
  maxWidth: 800,
  margin: '0 auto',
}));

export const BarTitle = styled('div')(({ theme }) => ({
  flexDirection: 'column',
  flex: 1,
  marginLeft: theme.spacing(2),
  overflowX: 'auto',
  whiteSpace: 'nowrap',
}));

export const ButtonContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(1),
  alignItems: 'center',
}));

export const DeleteButton = styled(Button)(({ theme }) => ({
  marginLeft: theme.spacing(1),
}));


export const RowOutline = styled('div')(({ theme }) => ({
  borderBottom: `1px solid ${theme.palette.action.disabledBackground}`,
  padding: `${theme.spacing(2)} 0 0 0`,
  float: 'left',
  width: '100%',
}));

export const HideSmall = styled('div')(({ theme }) => ({
  [theme.breakpoints.down('sm')]: {
    display: 'none',
  },
}));

export const NextListItem = styled('div', {
  shouldForwardProp: (prop) => prop !== 'left',
})(({ theme, left }) => ({
  position: 'fixed',
  top: '48%',
  maxWidth: theme.spacing(6),
  ...(left ? { left: 10 } : { right: 10 }),
}));

export const TagButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== 'isRemove',
})(({ theme, isRemove }) => ({
  color: theme.palette.secondary.main,
  borderColor: theme.palette.secondary.main,
  marginBottom: theme.spacing(1),
  ...(isRemove
    ? {
        color: theme.palette.error.main,
        borderColor: theme.palette.error.main,
      }
    : {}),
}));

// Comment-related styles -----------------------------------------------------

export const CommentContainerWrap = styled('div')(({ theme }) => ({
  width: '100%',
  marginTop: theme.spacing(),
}));

export const CommentsWrap = styled('div')(({ theme }) => ({
  minHeight: theme.spacing(4),
}));

export const CommentWrapTitle = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  width: '100%',
  alignItems: 'center',
  paddingTop: theme.spacing(2),
}));

export const CommentCount = styled('div')(({ theme }) => ({
  fontSize: 13,
  marginLeft: theme.spacing(1),
}));

export const ShowWrap = styled('div')(({ theme }) => ({
  margin: `${theme.spacing(1)} 0`,
  '& .MuiToggleButtonGroup-root': {
    marginLeft: theme.spacing(1),
    '& button': {
      padding: `0px 5px`,
    },
  },
}));

export const CommentBtnContainer = styled('div')(({ theme }) => ({
  marginTop: theme.spacing(2),
  display: 'flex',
  flexDirection: 'row',
  flexWrap: 'wrap',
  alignItems: 'center',
  justifyContent: 'flex-end',
}));

export const CommentBtn = styled(Button)(({ theme }) => ({
  marginRight: theme.spacing(1),
  marginBottom: theme.spacing(1),
}));

export const CreateCommentContainer = styled('div', {
  shouldForwardProp: (prop) => prop !== 'isMobile',
})(({ theme, isMobile }) => ({
  position: 'relative',
  width: isMobile ? `calc(100% + 76px)` : '100%',
  marginLeft: isMobile ? '-38px' : 'auto',
  marginRight: isMobile ? '-38px' : 'auto',
  marginTop: theme.spacing(2),
  display: 'flex',
  flexDirection: 'column',
}));

export const CreateCommentContainerModal = styled('div')({
  position: 'relative',
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
});

export const LeftIcon = styled('div')(({ theme }) => ({
  position: 'absolute',
  top: 15,
  left: 10,
  width: 40,
  height: 40,
  backgroundColor: theme.palette.action.disabled,
  borderRadius: 40,
  color: theme.palette.common.white,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  [theme.breakpoints.down('md')]: {
    display: 'none',
  },
}));

export const TextContainer = styled('div', {
  shouldForwardProp: (prop) => prop !== 'isModal',
})(({ theme, isModal }) => ({
  width: '80%',
  marginLeft: isModal ? '11%' : '10%',
  marginRight: '10%',
  paddingTop: theme.spacing(1),
  paddingBottom: theme.spacing(1),
}));

export const TextContainerMobile = styled('div')(({ theme }) => ({
  width: '100%',
  paddingTop: theme.spacing(1),
  paddingBottom: theme.spacing(1),
}));

export const TitleContainer = styled('div')({
  fontSize: 13,
});

export const CorrectionToggleContainer = styled('div')(({ theme }) => ({
  marginBottom: theme.spacing(2),

  '& .MuiToggleButtonGroup-root': {
    width: '100%',
  },
  '& .MuiToggleButtonGroup-root button': {
    width: '100%',
  },
}));

export const CorrectionBtnIcon = styled('span')(({ theme }) => ({
  marginRight: theme.spacing(1),
  display: 'inline-flex',
  alignItems: 'center',
}));

export const CommentTextField = styled('div')(({ theme }) => ({
  margin: `${theme.spacing(1)} 0`,
  '& .MuiTextField-root': {
    width: '100%',
  },
}));

export const SubmitButtons = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'flex-end',

  '& button': {
    marginRight: 0,
    marginLeft: theme.spacing(1),
    marginTop: theme.spacing(1),
  },
}));
